import { dedupeInFlightGet } from "@/utils/inFlightGetDedup";

interface SubscriptionGetOptions {
  allow404?: boolean;
}

/**
 * Fetch With429 Retry.
 */
async function fetchWith429Retry(
  endpoint: string,
  headers: Record<string, string>,
  retryCount = 0
): Promise<Response> {
  const response = await fetch(endpoint, {
    method: "GET",
    headers,
  });

  if (response.status === 429 && retryCount < 2) {
    const retryAfter = response.headers.get("Retry-After");
    const delay = retryAfter
      ? Math.min(Number.parseInt(retryAfter, 10) * 1000, 60_000)
      : Math.min(1000 * 2 ** retryCount, 10_000);
    await new Promise((resolve) => setTimeout(resolve, delay));
    return fetchWith429Retry(endpoint, headers, retryCount + 1);
  }

  return response;
}

/**
 * Deduplicated GET for subscription-service endpoints (shared across hooks/components).
 */
export function subscriptionHttpGet<T>(
  endpoint: string,
  options: SubscriptionGetOptions = {}
): Promise<T | null> {
  return dedupeInFlightGet(endpoint, async () => {
    const { getAuthHeader } = await import("@/config/api");
    const { getAuthToken } = await import("../assets/assetHelpers");
    const token = await getAuthToken();

    const response = await fetchWith429Retry(endpoint, getAuthHeader(token || undefined));

    if (options.allow404 && response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json() as Promise<T>;
  });
}
